import { useMemo, useState } from 'react';
import {
  useGetTenantsByIdMembersQuery,
  useDeleteTenantsByTenantIdMembersAndMemberIdMutation,
  usePatchTenantsByTenantIdMembersAndMemberIdRoleMutation,
  useGetUsersMeQuery,
  usePostTenantsByTenantIdMembersAndMemberIdResetPasswordMutation,
} from '@/services';
import { useTenant } from '@/contexts/tenant-context';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { MoreHorizontal, UserPlus, Shield, UserMinus } from 'lucide-react';
import { InviteMemberDialog } from './invite-member-dialog';
import { cn } from '@/lib/utils';

export function TenantMembers() {
  const { currentTenant } = useTenant();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [memberToReset, setMemberToReset] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const { data: currentUserData } = useGetUsersMeQuery();
  const { data, isLoading, refetch } = useGetTenantsByIdMembersQuery(
    { id: currentTenant?.id || '' },
    { skip: !currentTenant },
  );

  const [updateMemberRole, { isLoading: isUpdatingRole }] = usePatchTenantsByTenantIdMembersAndMemberIdRoleMutation();
  const [removeMember, { isLoading: isRemoving }] = useDeleteTenantsByTenantIdMembersAndMemberIdMutation();
  const [resetPasswordForMember] = usePostTenantsByTenantIdMembersAndMemberIdResetPasswordMutation();

  const members = useMemo(() => data?.data || [], [data]);
  const currentUser = currentUserData?.data || null;
  const isCurrentUserAdmin = useMemo(() => {
    return members.some(
      (member) => member.user_id === currentUser?.id && (member.role === 'admin' || member.role === 'owner'),
    );
  }, [members, currentUser]);

  const handleRoleChange = async (memberId: string, newRole: 'admin' | 'member') => {
    if (!currentTenant) return;

    try {
      await updateMemberRole({
        tenantId: currentTenant.id,
        memberId,
        body: { role: newRole },
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Member role updated successfully',
      });

      refetch();
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to update member role',
      });
    }
  };

  const handleRemoveMember = async () => {
    if (!currentTenant || !memberToRemove) return;

    try {
      await removeMember({
        tenantId: currentTenant.id,
        memberId: memberToRemove,
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });

      setMemberToRemove(null);
      refetch();
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to remove member',
      });
    }
  };

  if (!currentTenant) {
    return <div className="text-center py-10">Please select a tenant first</div>;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>Manage users who have access to this tenant.</CardDescription>
          </div>
          <Button onClick={() => setIsInviteDialogOpen(true)} size="sm">
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              No members found. Invite some team members to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>
                        {member.first_name?.charAt(0) || member?.email?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {member.first_name ? `${member.first_name} ${member.last_name || ''}` : member.email}{' '}
                        {currentUser && member.user_id === currentUser.id ? '(You)' : ''}
                      </div>
                      <div className="text-sm text-muted-foreground">{member.email}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Badge variant={member.status === 'active' ? 'default' : 'outline'} className="capitalize">
                      {member.status}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {member.role}
                    </Badge>

                    <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        disabled={
                          !isCurrentUserAdmin ||
                          member.user_id === currentUser?.id ||
                          (member.role === 'owner' && currentUser?.id !== member.user_id)
                        }
                      >
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isCurrentUserAdmin && (
                          <>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <Shield className="mr-2 h-4 w-4" />
                                <span>Change Role</span>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem
                                    onClick={() => handleRoleChange(member.id, 'admin')}
                                    disabled={member.role === 'admin' || isUpdatingRole}
                                  >
                                    Admin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleRoleChange(member.id, 'member')}
                                    disabled={member.role === 'member' || isUpdatingRole}
                                  >
                                    Member
                                  </DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setMemberToRemove(member.id)}
                              className="text-red-500 focus:text-red-500"
                            >
                              <UserMinus className="mr-2 h-4 w-4" />
                              <span>Remove Member</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setMemberToReset(member.id)} disabled={isResetting}>
                              <Shield className="mr-2 h-4 w-4" />
                              <span>Send Reset Password Email</span>
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <InviteMemberDialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen} onSuccess={refetch} />

      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this member from the tenant? They will lose access to all resources in
              this tenant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-red-500 hover:bg-red-600">
              {isRemoving ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!memberToReset} onOpenChange={(open) => !open && setMemberToReset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Reset Password Email</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send a password reset email to this member?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!currentTenant || !memberToReset) return;
                setIsResetting(true);
                try {
                  await resetPasswordForMember({
                    tenantId: currentTenant.id,
                    memberId: memberToReset,
                  }).unwrap();
                  toast({
                    title: 'Success',
                    description: 'Reset password email sent!',
                  });
                } catch (error) {
                  toast({
                    variant: 'error',
                    title: 'Error',
                    description: 'Failed to send reset password email',
                  });
                }
                setIsResetting(false);
                setMemberToReset(null);
              }}
              disabled={isResetting}
            >
              {isResetting ? 'Sending...' : 'Send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
